import { useCallback, useEffect, useMemo, useState } from 'react';
import { repositoryFactory } from '@data';

const EMPTY_PAGINATION = Object.freeze({
  page: 1,
  pageSize: 25,
  total: 0,
  totalPages: 1,
  hasPreviousPage: false,
  hasNextPage: false,
});

/**
 * Hook for loading email dispatch logs with filter and pagination support.
 */
export function useEmailDispatchLogs(filters = {}, enabled = true) {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);
  const [statusOptions, setStatusOptions] = useState([]);
  const [providerOptions, setProviderOptions] = useState([]);
  const [templateTypes, setTemplateTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const courseRepo = useMemo(() => repositoryFactory.getCourseRepository(), []);

  const normalizedFilters = useMemo(() => ({
    courseId: String(filters.courseId || '').trim(),
    status: String(filters.status || '').trim(),
    templateType: String(filters.templateType || '').trim(),
    provider: String(filters.provider || '').trim(),
    search: String(filters.search || '').trim(),
    page: Number.parseInt(filters.page, 10) || 1,
    pageSize: Number.parseInt(filters.pageSize, 10) || 25,
  }), [
    filters.courseId,
    filters.status,
    filters.templateType,
    filters.provider,
    filters.search,
    filters.page,
    filters.pageSize,
  ]);

  const loadLogs = useCallback(async () => {
    if (!enabled) {
      setLogs([]);
      setPagination(EMPTY_PAGINATION);
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await courseRepo.getEmailDispatchLogs(normalizedFilters);
      setLogs(Array.isArray(response?.logs) ? response.logs : []);
      setPagination(response?.pagination || EMPTY_PAGINATION);
      setStatusOptions(Array.isArray(response?.statusOptions) ? response.statusOptions : []);
      setProviderOptions(Array.isArray(response?.providerOptions) ? response.providerOptions : []);
      setTemplateTypes(Array.isArray(response?.templateTypes) ? response.templateTypes : []);
    } catch (err) {
      setError(err.message || 'Failed to load email logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [courseRepo, enabled, normalizedFilters]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  return {
    logs,
    pagination,
    statusOptions,
    providerOptions,
    templateTypes,
    loading,
    error,
    refresh: loadLogs,
    clearError: () => setError(null),
  };
}
